import { useEffect, useMemo } from "react";
import { useAuthUser } from "hooks/useAuthUser";
import {
  useGetMentoringsByMenteeLazyQuery,
  useGetUsersRoleByOrgLazyQuery,
  useAddMentoringMutation,
  useDeleteMentoringMutation,
} from "graphql/__generated__/hooks";
import { ROLES } from "types/user";
import { useAppSelector } from "hooks/useAppSelector";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";

export const useMentorList = () => {
  const { user } = useAuthUser();
  const organization = useAppSelector(organizationSelectedSelector);
  const [getMentoringsByMentee, { data, loading, error }] =
    useGetMentoringsByMenteeLazyQuery();
  const [getUsersRoleByOrg, { data: coachesData, loading: loadingCoaches }] =
    useGetUsersRoleByOrgLazyQuery();
  const [addMentoring, { loading: addingMentor }] = useAddMentoringMutation();
  const [deleteMentoring, { loading: deletingMentor }] =
    useDeleteMentoringMutation();

  const mentors = useMemo(() => {
    return (
      (data?.mentoring || []).map((m) => ({
        ...m.mentor,
        mentoring_id: m.mentoring_id,
      })) || []
    );
  }, [data]);

  const coaches = useMemo(() => {
    return (coachesData?.organization_user || []).map((ou) => ou.user) || [];
  }, [coachesData]);

  useEffect(() => {
    if (user?.user_id) {
      getMentoringsByMentee({ variables: { mentee: user.user_id } });
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (organization) {
      getUsersRoleByOrg({
        variables: { orgId: organization.org_id, role: ROLES.COACH },
      });
    }
  }, [organization]);

  const selectMentor = async (mentorId: string) => {
    if (!mentorId || !user?.user_id) return;
    await addMentoring({
      variables: { mentee: user.user_id, mentor: mentorId },
    });
    // refetch mentorings
    await getMentoringsByMentee({ variables: { mentee: user.user_id } });
  };

  const deleteMentor = async (mentoringId: string) => {
    if (!mentoringId || !user?.user_id) return;
    await deleteMentoring({ variables: { id: mentoringId } });
    await getMentoringsByMentee({ variables: { mentee: user.user_id } });
  };

  return {
    mentors,
    coaches,
    loading: loading || loadingCoaches || addingMentor || deletingMentor,
    error,
    selectMentor,
    deleteMentor,
  };
};
